import { supabase } from "@/supabase";

// Swell data layer. Follows the project service convention: each method returns a
// plain object ({ data, error } / { count, error }) with `error` as a message string,
// so views can fall back to mock data or toast on failure. Read-heavy (ingestion runs
// server-side in edge functions); writes are limited to human approval decisions.

const _HANDLE = "swell_brand_handle";
const _SOURCE = "swell_source";
const _OCCASION = "swell_occasion";
const _MARKET = "swell_market";

// -- lead row display helpers --
const swellInitials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const SWELL_COLORS = ["#6366f1", "#db2777", "#1a73e8", "#0b8043", "#9333ea", "#b45309", "#0891b2", "#e11d48"];
const swellColor = (name) => {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return SWELL_COLORS[h % SWELL_COLORS.length];
};
const swellMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return "$" + (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return "$" + Math.round(v / 1e3) + "K";
  return "$" + v;
};

// LinkedIn URLs from the AI verify step often arrive without a protocol ("linkedin.com/in/x"), which
// an <a href> resolves as a RELATIVE path (…/swell/linkedin.com/in/x). Normalised on write in the
// edge functions; also applied here so rows saved before that fix still open correctly.
const swellLinkedin = (v) => {
  if (typeof v !== "string") return null;
  let s = v.trim().replace(/^@+/, "").replace(/^\/+/, "").split("?")[0].split("#")[0];
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) s = "https://" + s.replace(/^\/\//, "");
  return s.replace(/^http:\/\//i, "https://").replace(/\/+$/, "");
};

// -- brand-level outreach (BD tracking) --
// Labels mirror the team's reach-out sheet verbatim, so the UI reads exactly like their tracker.
export const SWELL_BD_STATUSES = [
  { value: "no_contact", label: "No Contact" },
  { value: "reach_out", label: "Reach Out" },
  { value: "linkedin_sent", label: "LinkedIn Sent" },
  { value: "email_1_sent", label: "Email 1 Sent" },
  { value: "email_2_sent", label: "Email 2 Sent" },
  { value: "follow_up", label: "Follow-up" },
  { value: "unresponsive", label: "Unresponsive" },
  { value: "meeting", label: "Meeting" },
  { value: "pitch", label: "Pitch" },
  { value: "declined", label: "Declined" },
  { value: "converted", label: "Converted" },
  { value: "not_active", label: "Not Active" },
  { value: "in_house", label: "In House" },
];
export const SWELL_BD_PRIORITIES = [
  { value: "high", label: "High" },
  { value: "hold", label: "Hold" },
  { value: "new", label: "New" },
];
export const SWELL_CAMPAIGN_STATUSES = [
  { value: "none", label: "—" },
  { value: "active", label: "Active" },
  { value: "complete", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
];
export const swellBdStatusLabel = (v) => (SWELL_BD_STATUSES.find((s) => s.value === v) || {}).label || "No Contact";

// PostgREST returns an embedded one-to-one as an object OR a 1-element array depending on how it
// infers the relationship — normalise both, and give callers a usable default when there's no row yet.
const normalizeOutreach = (o) => {
  const r = Array.isArray(o) ? o[0] : o;
  if (!r) return { status: "no_contact", priority: "new", campaignStatus: "none", lastContactAt: null, lastContactLabel: null, nextSteps: null, clientFeedback: null, ownerId: null, ownerName: null, tracked: false };
  return {
    status: r.bd_status || "no_contact",
    priority: r.priority || "new",
    campaignStatus: r.campaign_status || "none",
    lastContactAt: r.last_contact_at || null,
    lastContactLabel: r.last_contact_label || null,
    nextSteps: r.next_steps || null,
    clientFeedback: r.client_feedback || null,
    ownerId: r.owner_id || null,
    ownerName: r.owner ? [r.owner.first_name, r.owner.last_name].filter(Boolean).join(" ") : null,
    tracked: true,
  };
};

// Derive the Instagram post shortcode from a media id (pk) → https://instagram.com/p/<code>/
const igShortcode = (pk) => {
  try {
    // eslint-disable-next-line no-undef
    let id = BigInt(String(pk).split("_")[0]);
    const abc = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
    let out = "";
    while (id > 0n) { out = abc[Number(id % 64n)] + out; id = id / 64n; }
    return out || null;
  } catch { return null; }
};

// Display string for estimated creator spend: a manual value, a p25–p75 range, a single point, or "—".
const swellSpendRange = ({ manual, dealValue, origin, low, high, point }) => {
  if (manual) return swellMoney(dealValue);
  if (origin === "news") return "—"; // news leads have no post data to price
  if (low && high && high > low) return swellMoney(low) + "–" + swellMoney(high);
  if (point) return swellMoney(point);
  return "—";
};

// Distributors for a set of brands, as "SA: UFS · EG: Manfoods · AE, BH, KW, OM, QA: Mohebi".
//
// Partners are keyed (brand, country), so one brand can have several — McDonald's has three across
// seven markets. A single name would have to pick one and present it as the answer, so the row shows
// the grouping instead. A country_id of null is the DEFAULT row: it covers every market the brand
// sells in that no explicit row names.
function distributorLines(rows, marketsByBrand) {
  const byBrand = new Map();
  for (const r of rows || []) {
    if (r.role !== "distributor" || r.status !== "confirmed") continue;
    const arr = byBrand.get(r.swell_brand_id) || [];
    arr.push(r);
    byBrand.set(r.swell_brand_id, arr);
  }
  const out = new Map();
  for (const [brandId, list] of byBrand) {
    const all = marketsByBrand.get(brandId) || [];
    const named = new Set(list.map((r) => r.code).filter(Boolean));
    const byGroup = new Map();
    for (const r of list) {
      const key = r.group_name || "—";
      const codes = byGroup.get(key) || [];
      if (r.code) codes.push(r.code);
      else codes.push(...all.filter((c) => !named.has(c)));   // the default row, spelled out
      byGroup.set(key, codes);
    }
    const parts = [...byGroup.entries()]
      .map(([name, codes]) => ({ name, codes: [...new Set(codes)].sort() }))
      .filter((p) => p.codes.length)
      .sort((a, z) => z.codes.length - a.codes.length);
    out.set(brandId, parts);
  }
  return out;
}

// One query for the partner rows, one for the markets each brand sells in.
async function loadDistributors(brandIds) {
  if (!brandIds.length) return new Map();
  const rows = [];
  for (let i = 0; i < brandIds.length; i += 200) {
    const chunk = brandIds.slice(i, i + 200);
    const { data } = await supabase
      .from("swell_brand_market_partner")
      .select("swell_brand_id, role, status, country:country_id(code), grp:group_id(name)")
      .in("swell_brand_id", chunk)
      .eq("role", "distributor")
      .eq("status", "confirmed");
    for (const r of data || []) {
      rows.push({
        swell_brand_id: r.swell_brand_id, role: r.role, status: r.status,
        code: r.country?.code ? String(r.country.code).toUpperCase() : null,
        group_name: r.grp?.name || null,
      });
    }
  }
  if (!rows.length) return new Map();

  // Which markets each brand sells in — country handles, plus what a regional handle implies.
  const markets = new Map();
  const need = [...new Set(rows.map((r) => r.swell_brand_id))];
  for (let i = 0; i < need.length; i += 200) {
    const { data } = await supabase
      .from("swell_brand_handle")
      .select("swell_brand_id, market_scope, approval, country:country_id(code)")
      .in("swell_brand_id", need.slice(i, i + 200))
      .neq("approval", "rejected");
    for (const h of data || []) {
      const set = markets.get(h.swell_brand_id) || new Set();
      const c = h.country?.code ? String(h.country.code).toUpperCase() : null;
      if (c && IN_MARKET_CODES.includes(c)) set.add(c);
      if (!c && (h.market_scope === "gcc" || h.market_scope === "mena")) {
        (h.market_scope === "gcc" ? GCC_CODES : IN_MARKET_CODES).forEach((x) => set.add(x));
      }
      markets.set(h.swell_brand_id, set);
    }
  }
  const asArrays = new Map([...markets].map(([k, v]) => [k, [...v].sort()]));
  return distributorLines(rows, asArrays);
}

const IN_MARKET_CODES = ["AE", "SA", "QA", "KW", "BH", "OM", "EG", "JO", "LB", "IQ", "MA"];
const GCC_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];

export const SwellService = {
  // -- Leads (scored) ------------------------------------------------------
  // Live swell_lead rows mapped to the shape the Leads view row expects.
  async getLeads() {
    const resp = { data: [], total: 0, error: null };
    const SELECT =
      "id, swell_brand_id, owner_id, pipeline_stage, origin, source_url, brief, brief_sources, enriched_at, spend_horizon, swell_score, signal_sources, why_now, recurring_window, occasions, occasion_count, top_creator, top_followers, creators_year, est_activity_tier, emv_estimate, spend_estimate, spend_low, spend_high, spend_deals, deal_value, " +
      "roster_matches, creator_count, window_days, surfaced_at, lead_country:country_id(code, name), owner:owner_id(first_name, last_name), " +
      "brand:swell_brand_id(name, category:category_id(name), parent:parent_group_id(id, name, type), handles:swell_brand_handle(approval, platform, operator:operator_group_id(id, name), country:country_id(code,name)), links:swell_brand_link(is_primary, b:brand_id(logo)), " +
      "outreach:swell_brand_outreach(bd_status, priority, campaign_status, last_contact_at, last_contact_label, next_steps, client_feedback, owner_id, owner:owner_id(first_name, last_name)))";

    // PostgREST caps a response at 1000 rows and there are more leads than that. An unpaged read
    // returned a SUBSET, but the KPI strip sums whatever was returned — so "Est. pipeline spend"
    // swung between ~$70M and ~$80M on every refresh, and the table itself was missing leads.
    //
    // swell_score alone is not a stable sort: hundreds of leads tie on the same score, so Postgres
    // was free to return a different set of them either side of the 1000-row cut each time, and the
    // scorer cron rewrites scores under us. Tie-break on id so paging can't skip or repeat a row.
    const SIZE = 1000;
    const rows = [];
    let count = 0;
    for (let from = 0; ; from += SIZE) {
      // count ONLY on the first page: { count: "exact" } makes Postgres count the whole table for
      // every request it's attached to, so asking on all five pages did the same full scan five
      // times for a number that can't change mid-read.
      const { data, error, count: c } = await supabase
        .from("swell_lead")
        .select(SELECT, from === 0 ? { count: "exact" } : undefined)
        .not("swell_score", "is", null)
        .order("swell_score", { ascending: false })
        .order("id", { ascending: true })
        .range(from, from + SIZE - 1);
      if (error) {
        resp.error = error.message;
        return resp;
      }
      if (c != null) count = c;
      rows.push(...(data || []));
      if (!data || data.length < SIZE) break;
    }
    const data = rows;
    resp.total = count || 0;
    // "New today" = surfaced since local midnight (a true calendar day, not a rolling 36h window).
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const startMs = startOfToday.getTime();
    // One read for the whole page rather than per row. Partners are (brand, country), so this also
    // needs the brand's markets to spell out what the default row covers.
    const brandIds = [...new Set((data || []).map((l) => l.swell_brand_id).filter(Boolean))];
    const distByBrand = await loadDistributors(brandIds);

    resp.data = (data || []).map((l) => {
      const b = l.brand || {};
      // logo: reuse the agency's own brand logo via the swell_brand → brand link (prefer primary)
      const links = b.links || [];
      const logoUrl = (links.find((x) => x.is_primary) || links[0] || {}).b?.logo || null;
      // News leads store their own event country (from the article). Occasion leads derive it from the
      // brand's APPROVED instagram handles: one approved country → show it; multiple/region-only → "GCC".
      let country;
      if (l.origin === "news") {
        country = l.lead_country?.code ? l.lead_country.code.toUpperCase() : "GCC";
      } else {
        const handles = (b.handles || []).filter((h) => h.platform === "instagram" && h.approval === "approved");
        const codes = [...new Set(handles.map((h) => h.country?.code).filter(Boolean))];
        country = codes.length === 1 ? codes[0] : "GCC";
      }
      // Market OPERATOR (who runs the brand here = who you pitch). Prefer the operator on the handle for
      // this lead's country; if the lead spans markets (GCC), show the single operator or "Multiple".
      let operatorName = null;
      if (l.origin !== "news") {
        const ah = (b.handles || []).filter((h) => h.platform === "instagram" && h.approval === "approved");
        const marketOp = ah.find((h) => h.country?.code === country && h.operator?.name)?.operator?.name;
        const distinctOps = [...new Set(ah.map((h) => h.operator?.name).filter(Boolean))];
        operatorName = marketOp || (distinctOps.length === 1 ? distinctOps[0] : distinctOps.length > 1 ? "Multiple operators" : null);
      }
      // The DISTRIBUTOR for this market — for McDonald's UAE that's Mohebi Martin Brower, not the
      // franchisee and not McDonald's Corporation.
      const distributorParts = distByBrand.get(l.swell_brand_id) || [];
      const cc = l.creator_count || 0;
      const rm = l.roster_matches || 0;
      const ratio = cc ? rm / cc : 0;
      const fit = ratio >= 0.5 ? "High" : ratio >= 0.2 ? "Med" : "Low";
      const isNew = l.surfaced_at ? new Date(l.surfaced_at).getTime() >= startMs : false;
      // Estimated creator spend (what they likely PAY creators for this occasion). Manual deal_value
      // overrides it. Shown as a p25–p75 range; news leads have no post data → "—".
      const manual = l.deal_value != null;
      const spendPt = manual ? Number(l.deal_value) : (Number(l.spend_estimate) || 0);
      const spendLow = Number(l.spend_low) || 0;
      const spendHigh = Number(l.spend_high) || 0;
      const spendRange = swellSpendRange({ manual, dealValue: l.deal_value, origin: l.origin, low: spendLow, high: spendHigh, point: spendPt });
      return {
        leadId: l.id, // unique per (brand, occasion) — a brand can appear in multiple rows
        id: l.swell_brand_id,
        stage: l.pipeline_stage || "none", // 'none' = not yet in pipeline
        ownerId: l.owner_id || null,
        ownerName: l.owner ? [l.owner.first_name, l.owner.last_name].filter(Boolean).join(" ") : null,
        name: b.name || "—",
        logo: swellInitials(b.name),
        logoUrl,
        color: swellColor(b.name),
        // news brands have no IG-derived category; fall back to the sector the brief inferred
        industry: b.category?.name || (l.origin === "news" && l.brief?.category && l.brief.category !== "unclear" ? l.brief.category : ""),
        country,
        score: l.swell_score,
        win: l.origin === "news" ? null : (l.window_days ?? 30), // news leads aren't window-based

        budget: spendRange, // display: "$40K–$70K", a manual value, or "—"
        emv: spendPt || 0, // raw point value for KPI sums (manual override wins)
        emvManual: manual, // true = a human-entered deal value (vs auto spend estimate)
        spendLow, spendHigh, spendDeals: l.spend_deals || 0,
        fit,
        fitN: cc ? `${rm} of ${cc} creators` : "—",
        surfacedAt: l.surfaced_at || null, // when the lead first surfaced — for "recent" sort
        isNew,
        // A lead is now ONE ROW PER BRAND (migration 20260911130000). `occasion` is the NEXT window
        // — what to pitch first — and `occasions` is everything they campaign for, which is the
        // evidence management asked to see on the row.
        occasion: l.recurring_window || "",
        occasions: Array.isArray(l.occasions) ? l.occasions : [],
        occasionCount: l.occasion_count || (Array.isArray(l.occasions) ? l.occasions.length : 0),
        // biggest creator who has run a campaign for them — why this brand qualifies as a lead
        topCreator: l.top_creator || null,
        topFollowers: l.top_followers || null,
        // distinct creators paid in the last 12 months — how big a roster they actually book
        creatorsYear: l.creators_year || 0,
        origin: l.origin || null,
        sourceUrl: l.source_url || null,
        // news leads carry their signal type in signal_sources (e.g. 'store_opening')
        newsType: l.origin === "news" && l.signal_sources && l.signal_sources.length ? l.signal_sources[0] : null,
        brief: l.brief || null,
        briefSources: Array.isArray(l.brief_sources) ? l.brief_sources : [],
        enriched: !!l.enriched_at,
        spendHorizon: l.origin === "news" ? (l.spend_horizon || null) : null, // imminent|near|far
        // news timing from the brief, if enriched (e.g. "launches July 2")
        newsTimeline: l.origin === "news" && l.brief?.timeline && l.brief.timeline !== "unclear" ? l.brief.timeline : null,
        why: l.why_now || "",
        recurring: !!l.recurring_window,
        recurLead: l.recurring_window ? `${l.window_days ?? ""}d out` : "",
        // Brand-level outreach (one record per BRAND, not per lead — mirrors the team's reach-out sheet).
        // PostgREST returns an embedded 1:1 as an object or a 1-element array depending on the relation.
        outreach: normalizeOutreach(b.outreach),
        groupId: b.parent?.id || null,     // parent company / holding group (null = independent)
        groupName: b.parent?.name || null,
        groupType: b.parent?.type ? { retail_group: "Retail group", fmcg: "FMCG group", independent: "Independent" }[b.parent.type] || "Parent group" : null,
        operatorName,                      // franchisee running this brand in this market
        // [{ name, codes:["AE","BH"] }] — grouped, because a brand can have several
        distributors: distributorParts,
      };
    });
    return resp;
  },

  // Full detail for one lead: score + band + window + spend + owner/operator, live score components,
  // detected creators (the campaign roster), and the news brief. Powers the lead-detail drawer.
  async getLeadDetail(leadId) {
    const resp = { lead: null, components: [], creators: [], posts: [], error: null };
    const [leadRes, compRes, crRes, postRes] = await Promise.all([
      supabase.from("swell_lead").select(
        "id, swell_brand_id, owner_id, pipeline_stage, origin, source_url, brief, brief_sources, enriched_at, spend_horizon, swell_score, band, why_now, recurring_window, window_days, occasion_id, roster_matches, creator_count, spend_estimate, spend_low, spend_high, spend_deals, emv_estimate, deal_value, surfaced_at, " +
          "owner:owner_id(first_name, last_name), " +
          "brand:swell_brand_id(name, category:category_id(name), parent:parent_group_id(id, name, type), handles:swell_brand_handle(approval, platform, operator:operator_group_id(id, name), country:country_id(code, name)), links:swell_brand_link(is_primary, b:brand_id(logo)), " +
          "outreach:swell_brand_outreach(bd_status, priority, campaign_status, last_contact_at, last_contact_label, next_steps, client_feedback, owner_id, owner:owner_id(first_name, last_name)))"
      ).eq("id", leadId).single(),
      supabase.from("swell_lead_score_component").select("component, value, detail").eq("lead_id", leadId),
      supabase.rpc("swell_lead_creators", { p_lead_id: leadId, p_limit: 24 }),
      supabase.rpc("swell_lead_posts", { p_lead_id: leadId, p_limit: 12 }),
    ]);
    if (leadRes.error) { resp.error = leadRes.error.message; return resp; }
    const l = leadRes.data; const b = l.brand || {};
    const links = b.links || [];
    const logoUrl = (links.find((x) => x.is_primary) || links[0] || {}).b?.logo || null;
    const ah = (b.handles || []).filter((h) => h.platform === "instagram" && h.approval === "approved");
    let country;
    if (l.origin === "news") country = "GCC";
    else { const codes = [...new Set(ah.map((h) => h.country?.code).filter(Boolean))]; country = codes.length === 1 ? codes[0] : "GCC"; }
    let operatorName = null;
    if (l.origin !== "news") {
      const marketOp = ah.find((h) => h.country?.code === country && h.operator?.name)?.operator?.name;
      const distinctOps = [...new Set(ah.map((h) => h.operator?.name).filter(Boolean))];
      operatorName = marketOp || (distinctOps.length === 1 ? distinctOps[0] : distinctOps.length > 1 ? "Multiple operators" : null);
    }
    const manual = l.deal_value != null;
    const spendLow = Number(l.spend_low) || 0, spendHigh = Number(l.spend_high) || 0;
    const spendPt = manual ? Number(l.deal_value) : (Number(l.spend_estimate) || 0);
    const cc = l.creator_count || 0, rm = l.roster_matches || 0;
    resp.lead = {
      leadId: l.id, brandId: l.swell_brand_id, name: b.name || "—", logo: swellInitials(b.name), logoUrl, color: swellColor(b.name),
      industry: b.category?.name || (l.origin === "news" && l.brief?.category ? l.brief.category : ""),
      country, score: l.swell_score, band: l.band || (l.swell_score >= 70 ? "A" : l.swell_score >= 48 ? "B" : "C"),
      win: l.origin === "news" ? null : (l.window_days ?? null),
      spendRange: swellSpendRange({ manual, dealValue: l.deal_value, origin: l.origin, low: spendLow, high: spendHigh, point: spendPt }),
      spendDeals: l.spend_deals || 0,
      outreach: normalizeOutreach(b.outreach), // brand-level BD state (not per lead)
      groupId: b.parent?.id || null, groupName: b.parent?.name || null, operatorName,
      distributors: [],   // filled by getLeadPartners() on open — the drawer loads one brand
      why: l.why_now || "", recurring: !!l.recurring_window, occasion: l.recurring_window || "",
      recurLead: l.recurring_window ? `${l.window_days ?? ""}d out` : "",
      origin: l.origin || null, sourceUrl: l.source_url || null, brief: l.brief || null,
      briefSources: Array.isArray(l.brief_sources) ? l.brief_sources : [],
      spendHorizon: l.origin === "news" ? (l.spend_horizon || null) : null,
      rosterMatches: rm, creatorCount: cc, fitN: cc ? `${rm} of ${cc} creators` : "—",
      fit: cc ? (rm / cc >= 0.5 ? "High" : rm / cc >= 0.2 ? "Med" : "Low") : "Low",
      ownerId: l.owner_id || null, ownerName: l.owner ? [l.owner.first_name, l.owner.last_name].filter(Boolean).join(" ") : null,
      stage: l.pipeline_stage || "none",
    };
    resp.components = (compRes.data || []).map((c) => ({ component: c.component, value: Number(c.value) || 0, detail: c.detail || "" }));
    resp.creators = (crRes.error ? [] : (crRes.data || [])).map((c) => ({
      handle: c.handle, posts: c.posts || 0, avgEng: Math.round(Number(c.avg_eng) || 0),
      connected: c.connected === true, followers: Number(c.followers) || 0, kind: c.kind || null, name: c.full_name || null,
    }));
    resp.posts = (postRes.error ? [] : (postRes.data || [])).map((p) => {
      const sc = igShortcode(p.id);
      const isReel = p.media_type === 2;
      return {
        id: p.id, handle: p.creator_handle, isReel, paid: p.is_paid === true,
        likes: Number(p.likes) || 0, plays: Number(p.plays) || 0, comments: Number(p.comments) || 0,
        caption: p.caption || "", takenAt: p.taken_at || null,
        permalink: sc ? `https://www.instagram.com/p/${sc}/` : (p.creator_handle ? `https://instagram.com/${p.creator_handle}` : null),
      };
    });
    return resp;
  },

  // Generate an outreach draft (email + DM) for a lead, grounded in its occasion/spend/creators.
  async getPitchDraft(leadId) {
    const resp = { draft: null, context: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-pitch-draft", { body: { lead_id: leadId } });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch (_e) { /* ignore */ }
      resp.error = msg; return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.draft = data?.draft || null; resp.context = data?.context || null;
    return resp;
  },

  // Contacts already stored for a brand (cached Apollo/CRM people to pitch), newest first.
  // Read via a staff-gated RPC because pulse_contact has RLS the frontend role can't satisfy.
  async getContacts(brandId) {
    if (!brandId) return { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_brand_contacts", { p_brand_id: brandId });
    if (error) return { data: [], error: error.message };
    return {
      data: (data || []).map((c) => ({
        id: c.id, name: c.name, title: c.title || c.seniority || null, company: c.company || null, source: c.source || null,
        init: swellInitials(c.name), color: swellColor(c.name),
        email: c.email || null, emailVerified: !!c.email_verified,
        emailAvailable: c.email_available !== false, // Apollo has an email to reveal (gates the Reveal button)
        linkedin: swellLinkedin(c.linkedin_url), phone: c.phone || null, location: c.location || c.country || null,
        scope: c.scope || "brand", revealing: false,
        // on the brand's Reach outs row? none | active | dead
        reachoutState: c.reachout_state || "none",
        // freshness check (AI web search) — confirmed | changed | not_found | null(unchecked)
        verifyStatus: c.verify_status || null, verifyNote: c.verify_note || null,
        currentTitle: c.current_title || null, verifySources: Array.isArray(c.verify_sources) ? c.verify_sources : [],
        verifiedAt: c.verified_at || null, verifying: false,
        pitchRank: c.pitch_rank || null, pitchReason: c.pitch_reason || null, pitchBest: !!c.pitch_best, // saved "who to pitch" result
        brief: c.brief || null, showDetails: false, // AI profile (background / pitch angle / highlights)
      })),
      error: null,
    };
  },

  // Full directory of Swell contacts across all brands (for the Contacts screen).
  async getAllContacts() {
    const { data, error } = await supabase.rpc("swell_all_contacts", { p_limit: 1000 });
    if (error) return { data: [], error: error.message };
    return {
      data: (data || []).map((c) => ({
        id: c.id, name: c.name, title: c.title || c.seniority || null,
        company: c.company || null, source: c.source || null,
        brandId: c.brand_id || null, brandName: c.brand_name || c.company || "—",
        init: swellInitials(c.name), color: swellColor(c.name),
        email: c.email || null, emailVerified: !!c.email_verified,
        emailAvailable: c.email_available !== false,
        linkedin: swellLinkedin(c.linkedin_url), phone: c.phone || null,
        location: c.location || c.country || null,
        updatedAt: c.updated_at || null, revealing: false,
      })),
      error: null,
    };
  },

  // Verify ONE contact is still current (AI web search) before revealing. Costs an OpenAI web_search
  // (~$0.02), NOT an Apollo credit; result is cached on the contact.
  async verifyContact(contactId) {
    const resp = { status: null, note: null, currentTitle: null, sources: [], verifiedAt: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-verify-contact", { body: { contact_id: contactId } });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error + (ctx.detail ? ": " + ctx.detail : ""); } catch (_e) { /* ignore */ }
      resp.error = msg; return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.status = data?.verify_status || null; resp.note = data?.verify_note || null;
    resp.currentTitle = data?.current_title || null;
    resp.sources = Array.isArray(data?.verify_sources) ? data.verify_sources : [];
    resp.verifiedAt = data?.verified_at || null;
    resp.linkedin = swellLinkedin(data?.linkedin); // web search may find a LinkedIn Apollo didn't have
    resp.location = data?.location || null; // …and their location
    resp.brief = data?.brief || null; // …and a fuller background / pitch-angle brief
    return resp;
  },

  // "Who should I pitch?" — AI ranks the lead's contacts (cheap pass) then web-searches the top 5 to
  // pick the best. Returns [{id, rank, reason, is_best, verify_status, verify_note, current_title, verify_sources}].
  // OpenAI cost only, no Apollo credits.
  // Same ranking from a reach out. ONE call for the whole conversation, scoped to the people
  // actually attached to it — not one call per brand, which would leave a "best" per brand and let
  // the last call overwrite the group-level contacts the previous ones just ranked.
  async rankReachoutContacts(reachoutId) {
    return this.rankContacts(null, { reachoutId });
  },

  async rankContacts(leadId, { brandId = null, reachoutId = null } = {}) {
    const resp = { ranked: [], bestId: null, note: null, error: null };
    const body = leadId ? { lead_id: leadId }
      : reachoutId ? { reachout_id: reachoutId }
      : { swell_brand_id: brandId };
    const { data, error } = await supabase.functions.invoke("swell-rank-contacts", { body });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error + (ctx.detail ? ": " + ctx.detail : ""); } catch (_e) { /* ignore */ }
      resp.error = msg; return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.ranked = Array.isArray(data?.ranked) ? data.ranked : [];
    resp.bestId = data?.best_id ?? null; resp.note = data?.note ?? null;
    return resp;
  },

  // Reveal ONE contact's email (spends an Apollo credit); result is cached, so re-reveals are free.
  async revealContact(contactId) {
    const resp = { email: null, email_verified: false, phone: null, linkedin: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-reveal-contact", { body: { contact_id: contactId } });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error + (ctx.detail ? ": " + ctx.detail : ""); } catch (_e) { /* ignore */ }
      resp.error = msg; return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.email = data?.email || null; resp.email_verified = !!data?.email_verified;
    resp.phone = data?.phone || null; resp.linkedin = swellLinkedin(data?.linkedin);
    return resp;
  },

  // Discover who to contact for a lead via Apollo (targets the market operator, else the brand).
  // Spends Apollo credits, so this is an explicit on-demand action; results are cached in pulse_contact.
  async findContacts(leadId, target = "brand") {
    const resp = { data: [], company: null, target: null, note: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-find-contacts", { body: { lead_id: leadId, target } });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error + (ctx.detail ? ": " + ctx.detail : ""); } catch (_e) { /* ignore */ }
      resp.error = msg; return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.data = data?.contacts || []; resp.company = data?.company || null;
    resp.target = data?.target || null; resp.note = data?.note || null;
    return resp;
  },

  // Log good/bad-lead feedback (tunes what Swell surfaces).
  async submitLeadFeedback(leadId, good, userId = null) {
    const { error } = await supabase.from("swell_lead_feedback").insert({ lead_id: leadId, outcome: good ? "good_lead" : "bad_lead", user_id: userId || null });
    return { error: error ? error.message : null };
  },

  // Group detail: one parent group, its brands-on-watchlist (from live leads), shared creators
  // (2+ brands, via RPC), seasons the group activates in, and the group marketing contact.
  async getGroup(groupId) {
    const resp = { group: null, brands: [], sharedCreators: [], seasons: [], contact: null, ownerName: null, stats: { top: 0, avg: 0, count: 0, active: 0 }, error: null };
    const [grpRes, leadRes, scRes] = await Promise.all([
      supabase.from("swell_parent_group").select("id, name, type, description, contact:marketing_contact_id(id, name, title, company)").eq("id", groupId).single(),
      supabase.from("swell_lead")
        .select("id, swell_brand_id, swell_score, window_days, recurring_window, occasions, pipeline_stage, origin, owner:owner_id(first_name, last_name), " +
          "brand:swell_brand_id!inner(name, parent_group_id, category:category_id(name), handles:swell_brand_handle(platform, approval, country:country_id(code,name)), links:swell_brand_link(is_primary, b:brand_id(logo)))")
        .eq("brand.parent_group_id", groupId).not("swell_score", "is", null).order("swell_score", { ascending: false }),
      supabase.rpc("swell_group_shared_creators", { p_group_id: groupId, p_limit: 30 }),
    ]);
    if (grpRes.error) { resp.error = grpRes.error.message; return resp; }
    const g = grpRes.data;
    const typeLabel = { retail_group: "GCC Retail Group", fmcg: "FMCG Group", independent: "Independent" }[g.type] || "Parent group";
    resp.group = { id: g.id, name: g.name, type: g.type, typeLabel, description: g.description || null, logo: swellInitials(g.name), color: swellColor(g.name) };
    if (g.contact) resp.contact = { name: g.contact.name, title: g.contact.title || g.contact.company || null, init: swellInitials(g.contact.name), col: swellColor(g.contact.name) };

    // one row per brand (its top-scored lead), collecting occasions + owner across the brand's leads
    const leads = leadRes.data || [];
    const byBrand = new Map();
    const seasons = new Set();
    const ownerCount = new Map();
    for (const l of leads) {
      // Every occasion the brand campaigns for, not just its next window — a lead is one row per
      // brand now, so recurring_window alone would show the group activating in one month a year.
      if (Array.isArray(l.occasions) && l.occasions.length) l.occasions.forEach((o) => o && seasons.add(o));
      else if (l.recurring_window) seasons.add(l.recurring_window);
      const nm = l.owner ? [l.owner.first_name, l.owner.last_name].filter(Boolean).join(" ") : null;
      if (nm) ownerCount.set(nm, (ownerCount.get(nm) || 0) + 1);
      if (byBrand.has(l.swell_brand_id)) continue; // first = highest score
      const b = l.brand || {};
      const links = b.links || [];
      const logoUrl = (links.find((x) => x.is_primary) || links[0] || {}).b?.logo || null;
      const handles = (b.handles || []).filter((h) => h.platform === "instagram" && h.approval === "approved");
      const codes = [...new Set(handles.map((h) => h.country?.code).filter(Boolean))];
      byBrand.set(l.swell_brand_id, {
        brandId: l.swell_brand_id, leadId: l.id, name: b.name || "—",
        logo: swellInitials(b.name), logoUrl, color: swellColor(b.name),
        industry: b.category?.name || "", country: codes.length === 1 ? codes[0] : "GCC",
        score: l.swell_score || 0, win: l.origin === "news" ? null : (l.window_days ?? null),
        stage: l.pipeline_stage && l.pipeline_stage !== "none" ? l.pipeline_stage : null,
      });
    }
    resp.brands = [...byBrand.values()];
    resp.seasons = [...seasons];
    const scores = resp.brands.map((x) => x.score);
    resp.stats = {
      count: resp.brands.length,
      active: resp.brands.filter((x) => x.stage || (x.win != null && x.win <= 14)).length,
      top: scores.length ? Math.max(...scores) : 0,
      avg: scores.length ? Math.round(scores.reduce((a, c) => a + c, 0) / scores.length) : 0,
    };
    resp.ownerName = [...ownerCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    if (!scRes.error) {
      resp.sharedCreators = (scRes.data || []).map((c) => ({
        handle: c.handle, brandCount: c.brand_count || 0,
        brands: Array.isArray(c.brands) ? c.brands : [],
        name: c.full_name || null, followers: Number(c.followers) || 0, igCategory: c.ig_category || null,
      }));
    }
    return resp;
  },

  // Assign (or clear) a lead's owner. Admin / all-scope only (enforced by RLS on swell_lead).
  async assignLead(leadId, userId) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_lead").update({ owner_id: userId || null }).eq("id", leadId);
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Reach Outs table: full parity with the team's sheet, one row per BRAND --------------------
  // Every tracked brand appears (not just contacted ones), so this fully replaces the spreadsheet.
  // Outreach fields are left-joined — a brand with no outreach row yet shows blank/defaults.
  async getReachOuts() {
    const resp = { data: [], error: null };
    // PostgREST caps a response at 1000 rows. swell_brand is bigger than that, so an unpaged read
    // silently truncates (alphabetically — everything past ~"S" vanished, taking its outreach with
    // it). Page every table until exhausted.
    const fetchAll = async (table, select, tweak) => {
      const out = [];
      const SIZE = 1000;
      for (let from = 0; ; from += SIZE) {
        let q = supabase.from(table).select(select).range(from, from + SIZE - 1);
        if (tweak) q = tweak(q);
        const { data, error } = await q;
        if (error) throw new Error(error.message);
        out.push(...(data || []));
        if (!data || data.length < SIZE) break;
      }
      return out;
    };

    // flagged contacts, read live from pulse_contact (never a snapshot)
    const { data: flagged } = await supabase.rpc("swell_reachout_contacts");
    const cByBrand = new Map();
    for (const p of flagged || []) {
      const e = cByBrand.get(p.swell_brand_id) || { active: [], dead: [] };
      e[p.state === "dead" ? "dead" : "active"].push({
        id: p.id, name: p.name, title: p.title || "", email: p.email || "",
        emailAvailable: p.email_available === true, linkedin: swellLinkedin(p.linkedin_url) || "",
        verifyStatus: p.verify_status || null,
      });
      cByBrand.set(p.swell_brand_id, e);
    }

    let brands, outreach, leads;
    try {
      [brands, outreach, leads] = await Promise.all([
        fetchAll("swell_brand", "id, name, website, parent:parent_group_id(id, name), category:category_id(name)", (q) => q.order("id")),
        fetchAll("swell_brand_outreach", "*, owner:owner_id(first_name, last_name)", (q) => q.order("swell_brand_id")),
        fetchAll("swell_lead", "id, swell_brand_id, swell_score, recurring_window", (q) => q.not("swell_score", "is", null).order("id")),
      ]);
    } catch (e) { resp.error = e.message; return resp; }

    const brandsRes = { data: brands }, outreachRes = { data: outreach }, leadsRes = { data: leads };
    const oByBrand = new Map((outreachRes.data || []).map((o) => [o.swell_brand_id, o]));
    const lByBrand = new Map();
    for (const l of leadsRes.data || []) {
      const e = lByBrand.get(l.swell_brand_id) || { best: 0, occ: "", count: 0, leadId: null };
      e.count++;
      // top-scoring lead = the one the brand row links to (clicking the name opens it)
      if ((l.swell_score || 0) > e.best) { e.best = l.swell_score || 0; e.occ = l.recurring_window || ""; e.leadId = l.id; }
      lByBrand.set(l.swell_brand_id, e);
    }
    resp.data = (brandsRes.data || []).map((b) => {
      const o = oByBrand.get(b.id) || {};
      const agg = lByBrand.get(b.id) || { best: 0, occ: "", count: 0, leadId: null };
      return {
        brandId: b.id,
        leadId: agg.leadId,   // top lead — opening the brand opens this one
        brand: b.name || "—",
        group: b.parent?.name || "",
        groupId: b.parent?.id || null,
        category: b.category?.name || "",
        score: agg.best, occasion: agg.occ, leadCount: agg.count,
        // sheet columns, in sheet order
        agency: o.agency || "",
        ownerId: o.owner_id || null,
        ownerName: o.owner ? [o.owner.first_name, o.owner.last_name].filter(Boolean).join(" ") : "",
        lastContactLabel: o.last_contact_label || "",
        lastContactAt: o.last_contact_at || null,
        priority: o.priority || "new",
        status: o.bd_status || "no_contact",
        campaignStatus: o.campaign_status || "none",
        proactivePitch: o.proactive_pitch || "",
        proactivePitchLink: o.proactive_pitch_link || "",
        notRetained: o.not_retained || "",
        offerStrategy: o.offer_strategy || "",
        offerAmbassador: o.offer_ambassador || "",
        clientFeedback: o.client_feedback || "",
        nextSteps: o.next_steps || "",
        // flagged contacts (live from pulse_contact) + the free-text legacy/imported blobs
        contacts: (cByBrand.get(b.id) || {}).active || [],
        deadContacts: (cByBrand.get(b.id) || {}).dead || [],
        contactNotes: o.contact_notes || "",
        deadLeads: o.dead_leads || "",
        ccaJudgeInvite: o.cca_judge_invite || "",
        ccaB1Contacts: o.cca_b1_contacts || "", ccaB1Count: o.cca_b1_count ?? "", ccaB1Status: o.cca_b1_status || "",
        ccaB2Contacts: o.cca_b2_contacts || "", ccaB2Count: o.cca_b2_count ?? "", ccaB2Status: o.cca_b2_status || "",
        bouncedLeft: o.bounced_left || "",
        ccaLinkedin: o.cca_linkedin || "",
        awardsInvite: o.awards_invite || "", awardsConfirmed: o.awards_confirmed || "", awardsDeclined: o.awards_declined || "",
        bb2026: o.bb_2026 || "",
        followUpNote: o.follow_up_note || "",
        statusNote: o.status_note || "",
        dealValue: o.deal_value != null ? Number(o.deal_value) : null,
        tracked: !!oByBrand.get(b.id),
      };
    }).sort((a, b) => a.brand.localeCompare(b.brand));
    return resp;
  },

  // Create a contact by hand (we already know them). Becomes a normal pulse_contact, so it can be
  // verified/ranked/pitched like a found one. Returns the new (or matched existing) contact id.
  // Pass EITHER a brandId or a groupId. A group contact (the holding company's CMO, say) shows up
  // for every brand under that group instead of being re-typed against each one.
  async addManualContact(brandId, { name, title = "", email = "", linkedin = "", phone = "", toReachouts = true, groupId = null } = {}) {
    const resp = { id: null, error: null };
    if (!brandId && !groupId) { resp.error = "a brand or a group is required"; return resp; }
    if (!String(name || "").trim()) { resp.error = "name required"; return resp; }
    const { data, error } = await supabase.rpc("swell_add_manual_contact", {
      p_brand_id: groupId ? null : brandId, p_group_id: groupId || null,
      p_name: name, p_title: title, p_email: email,
      p_linkedin: linkedin, p_phone: phone, p_reachout: toReachouts,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.id = data ?? null;
    return resp;
  },

  // Reach outs contacts are a FLAG on pulse_contact, not a copy — so email/LinkedIn shown in the
  // table are always the live values (revealing later just works). state = none | active | dead.
  async setContactReachout(contactId, state) {
    const { error } = await supabase.rpc("swell_set_contact_reachout", { p_contact_id: contactId, p_state: state });
    return { error: error ? error.message : null };
  },

  // Patch any subset of outreach fields by DB column name. Omitted keys stay as they are; an
  // explicit null clears the field. Creates the brand's outreach row on first write.
  // Add a brand to Reach outs by hand. Brand can be an existing id (picked from autocomplete) or a
  // typed name — unknown names create the brand. Group is find-or-create by name. The caller becomes
  // the BD lead automatically (an existing owner is never overwritten). Returns the brand id.
  async addReachout({ brandId = null, brandName = "", group = "", sm = "" } = {}) {
    const resp = { brandId: null, error: null };
    if (!brandId && !String(brandName || "").trim()) { resp.error = "Brand name is required"; return resp; }
    const { data, error } = await supabase.rpc("swell_add_reachout", {
      p_brand_name: String(brandName || "").trim(),
      p_group_name: String(group || "").trim() || null,
      p_agency: String(sm || "").trim() || null,
      p_brand_id: brandId || null,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.brandId = data ?? null;
    return resp;
  },

  // "Ahmer contacted <Group> for X, Y, Z" — add several brands at once, sharing group + SM, all
  // owned by the caller. Brands already owned by someone else are skipped and returned in `blocked`
  // (partial success is deliberate; see the migration).
  async addReachoutBulk({ brands = [], group = "", sm = "" } = {}) {
    const resp = { added: [], blocked: [], error: null };
    const list = (brands || [])
      .map((b) => ({ id: b.id || null, name: String(b.name || "").trim() }))
      .filter((b) => b.id || b.name);
    if (!list.length) { resp.error = "Add at least one brand"; return resp; }
    const { data, error } = await supabase.rpc("swell_add_reachout_bulk", {
      p_brands: list,
      p_group_name: String(group || "").trim() || null,
      p_agency: String(sm || "").trim() || null,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.added = data?.added || [];
    resp.blocked = data?.blocked || [];
    return resp;
  },

  // -- Reach-out EVENTS: "Ahmer contacted <Group> for X, Y, Z" ------------------------------------
  // One row per contact, listing its brands. A group can have many; per-brand status still lives in
  // swell_brand_outreach and is shown when a row is expanded.
  async getReachoutEvents() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_reachout_list");
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((r) => ({
      id: r.id,
      group: r.group_name || "",
      groupId: r.group_id || null,
      sm: r.agency || "",
      ownerId: r.owner_id || null,
      ownerName: r.owner_name || "",
      contactedAt: r.contacted_at || null,
      notes: r.notes || "",
      markets: r.markets || [],
      brands: (r.brands || []).map((b) => ({
        id: b.id, name: b.name, status: b.status || "no_contact", ownerId: b.ownerId || null,
      })),
    }));
    return resp;
  },

  async createReachout({ brands = [], group = "", sm = "", notes = "", markets = [] } = {}) {
    const resp = { id: null, added: [], blocked: [], error: null };
    const list = (brands || [])
      .map((b) => ({ id: b.id || null, name: String(b.name || "").trim() }))
      .filter((b) => b.id || b.name);
    if (!list.length) { resp.error = "Add at least one brand"; return resp; }
    const { data, error } = await supabase.rpc("swell_create_reachout", {
      p_brands: list,
      p_group_name: String(group || "").trim() || null,
      p_agency: String(sm || "").trim() || null,
      p_notes: String(notes || "").trim() || null,
      p_markets: markets || [],
    });
    if (error) { resp.error = error.message; return resp; }
    resp.id = data?.id ?? null;
    resp.added = data?.added || [];
    resp.blocked = data?.blocked || [];
    return resp;
  },

  // Add one more brand to an existing reach out. Group / SM / BD lead are inherited from the event,
  // so the caller only supplies the brand. `keptOwner` is set when the brand already had a
  // different BD lead — it stays with them.
  async addBrandToReachout(reachoutId, { id = null, name = "" } = {}) {
    const resp = { brand: null, keptOwner: null, error: null };
    if (!reachoutId || (!id && !String(name || "").trim())) { resp.error = "Brand required"; return resp; }
    const { data, error } = await supabase.rpc("swell_reachout_add_brand", {
      p_reachout_id: reachoutId, p_brand_id: id || null, p_brand_name: String(name || "").trim() || null,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.brand = { id: data?.id, name: data?.name };
    resp.keptOwner = data?.keptOwner || null;
    return resp;
  },

  // Brand typeahead for the Add-to-reach-out dialog. Queries the table directly rather than
  // filtering a client-side list, so it works on screens that never load all ~1,900 brands.
  async searchBrands(query, limit = 8) {
    const resp = { data: [], error: null };
    const q = (query || "").trim();
    if (!q) return resp;
    const { data, error } = await supabase
      .from("swell_brand")
      .select("id, name")
      .ilike("name", `%${q}%`)
      .order("name", { ascending: true })
      .limit(limit);
    if (error) { resp.error = error.message; return resp; }
    resp.data = data || [];
    return resp;
  },

  // Patch a reach out's OWN fields — agency (SM), owner_id, group (by name), notes. Brand-level
  // columns still go through patchOutreach.
  async patchReachout(reachoutId, patch) {
    if (!reachoutId || !patch || !Object.keys(patch).length) return { error: null };
    const { error } = await supabase.rpc("swell_patch_reachout", {
      p_reachout_id: reachoutId, p_patch: patch,
    });
    return { error: error ? error.message : null };
  },

  // Markets a reach out covers: 'GCC' / 'MENA' / ISO-2 codes. Whole-array write.
  async setReachoutMarkets(reachoutId, markets) {
    const { error } = await supabase.rpc("swell_set_reachout_markets", {
      p_reachout_id: reachoutId, p_markets: markets || [],
    });
    return { error: error ? error.message : null };
  },

  // Apollo lookup by BRAND (the edge function already accepts swell_brand_id, so a reach out with
  // no scored lead can still search). target: 'brand' | 'group'.
  // reachoutId is optional: when given, Apollo's people search is gated to that reach out's Market
  // (an Egypt conversation shouldn't surface Dubai marketers). The function widens to the whole GCC
  // by itself if the narrow search finds nobody, so passing it can only help.
  async findContactsForBrand(brandId, target = "brand", reachoutId = null) {
    const resp = { data: [], error: null, note: null };
    if (!brandId) { resp.error = "No brand to search"; return resp; }
    const { data, error } = await supabase.functions.invoke("swell-find-contacts", {
      body: { swell_brand_id: brandId, target, reachout_id: reachoutId || undefined },
    });
    if (error) {
      // supabase-js collapses EVERY non-2xx into "Edge Function returned a non-2xx status code"
      // and hides the real body on error.context. Without this, "APOLLO_API_KEY not set",
      // "staff only" and "Apollo lookup failed" all look like the same meaningless string.
      let msg = error.message || String(error);
      try {
        const ctx = await error.context?.json?.();
        if (ctx?.error) msg = ctx.error + (ctx.detail ? ": " + ctx.detail : "");
      } catch (_e) { /* body wasn't JSON — keep the generic message */ }
      resp.error = msg;
      return resp;
    }
    if (data && data.error) { resp.error = data.error; return resp; }
    resp.data = data?.contacts || [];
    // The function reports its SOFT failures as 200 + note, not as an error: no Apollo company
    // match, no group to search, no marketing contacts. Dropping it is why "Find contacts" looked
    // like it silently did nothing.
    resp.note = data?.note || null;
    return resp;
  },

  // -- Contacts ON a reach out ---------------------------------------------------------------
  // Per conversation, not per brand: the same brand's GCC and Egypt reach outs involve different
  // people. Contacts stay in pulse_contact, so reveals/verification show through live.
  async getReachoutContacts(reachoutId) {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_reachout_contact_list", { p_reachout_id: reachoutId });
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((c) => ({
      id: c.id, name: c.name, title: c.current_title || c.title || "", company: c.company || "",
      email: c.email || "", emailVerified: c.email_verified === true,
      emailAvailable: c.email_available === true, emailStatus: c.email_status || null,
      linkedin: swellLinkedin(c.linkedin_url) || "", phone: c.phone || "",
      verifyStatus: c.verify_status || null, pitchBest: c.pitch_best === true, pitchRank: c.pitch_rank ?? null,
      pitchReason: c.pitch_reason || null,
      // what this person is recorded against — one of the reach out's brands, or the group
      attachedTo: c.attached_to || "", attachedKind: c.attached_kind || null,
      state: c.state || "active",
    }));
    return resp;
  },

  // Free: people we already hold for this reach out's brands or their group.
  async getReachoutContactSuggestions(reachoutId, limit = 25) {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_reachout_contact_suggestions", {
      p_reachout_id: reachoutId, p_limit: limit,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((c) => ({
      id: c.id, name: c.name, title: c.title || "", company: c.company || "",
      email: c.email || "", emailAvailable: c.email_available === true,
      linkedin: swellLinkedin(c.linkedin_url) || "",
      verifyStatus: c.verify_status || null, pitchBest: c.pitch_best === true, via: c.via || "brand",
      pitchReason: c.pitch_reason || null, attachedTo: c.attached_to || "",
    }));
    return resp;
  },

  // state: 'active' | 'dead' | 'none' (detach)
  async setReachoutContact(reachoutId, contactId, state = "active") {
    const { error } = await supabase.rpc("swell_reachout_set_contact", {
      p_reachout_id: reachoutId, p_contact_id: contactId, p_state: state,
    });
    return { error: error ? error.message : null };
  },

  // Every reach out's contacts in one call, so the table can render them inline (not just a count).
  async getReachoutContactsAll() {
    const resp = { data: new Map(), error: null };
    const { data, error } = await supabase.rpc("swell_reachout_contacts_all");
    if (error) { resp.error = error.message; return resp; }
    const shape = (p) => ({
      id: p.id, name: p.name || "", title: p.title || "",
      email: p.email || "", linkedin: swellLinkedin(p.linkedin) || "",
      pitchBest: p.pitchBest === true, pitchReason: p.pitchReason || null,
      attachedTo: p.attachedTo || "", attachedKind: p.attachedKind || null,
    });
    for (const r of data || []) {
      resp.data.set(r.reachout_id, {
        contacts: (r.contacts || []).map(shape),
        deadContacts: (r.dead || []).map(shape),
      });
    }
    return resp;
  },

  // Delete a whole reach out. Brand links cascade; each brand's own outreach row (status, feedback,
  // contacts) is deliberately left intact — this removes the record of a conversation, not the brand.
  // RLS decides who may: admins, 'all' scope, or the reach out's own BD lead.
  async deleteReachout(reachoutId) {
    const { error } = await supabase.from("swell_reachout").delete().eq("id", reachoutId);
    return { error: error ? error.message : null };
  },

  // Unlink a brand from a reach out. The brand's own outreach row is untouched — this only says it
  // wasn't part of that conversation.
  async removeBrandFromReachout(reachoutId, brandId) {
    const { error } = await supabase.rpc("swell_reachout_remove_brand", {
      p_reachout_id: reachoutId, p_brand_id: brandId,
    });
    return { error: error ? error.message : null };
  },

  async patchOutreach(brandId, patch) {
    const resp = { error: null };
    if (!brandId || !patch || !Object.keys(patch).length) return resp;
    const { error } = await supabase.rpc("swell_patch_outreach", { p_brand_id: brandId, p_patch: patch });
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Pipeline: ONE CARD PER BRAND, driven by brand outreach (not per-occasion leads) ------------
  // A brand with 5 occasion leads is still one BD conversation, so it appears once. The card shows
  // its best lead's score/occasion for context and sums the brand's spend estimate across leads.
  async getPipeline() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from("swell_brand_outreach")
      .select(
        "swell_brand_id, bd_status, priority, last_contact_at, last_contact_label, next_steps, owner_id, deal_value, updated_at, " +
          "brand:swell_brand_id(name, links:swell_brand_link(is_primary, b:brand_id(logo))), owner:owner_id(first_name, last_name)"
      )
      .neq("bd_status", "no_contact");
    if (error) { resp.error = error.message; return resp; }
    const rows = data || [];
    if (!rows.length) return resp;

    // Pull the leads for these brands to derive score / occasion / spend context for each card.
    const ids = rows.map((r) => r.swell_brand_id);
    const { data: leadRows } = await supabase
      .from("swell_lead")
      .select("swell_brand_id, swell_score, recurring_window, origin, spend_estimate, spend_low, spend_high, deal_value")
      .in("swell_brand_id", ids)
      .not("swell_score", "is", null);
    // Value the card on the brand's BEST lead, NOT the sum of every occasion. A brand can carry a
    // dozen scored occasions across the year, but you're pitching one campaign at a time — summing
    // them all would treat the whole calendar as simultaneous revenue and wildly overstate the
    // pipeline. Best lead = the conservative, defensible number (and matches the old per-lead card).
    const byBrand = new Map();
    for (const l of leadRows || []) {
      const e = byBrand.get(l.swell_brand_id) || { best: null, count: 0 };
      e.count++;
      if (!e.best || (l.swell_score || 0) > (e.best.swell_score || 0)) e.best = l;
      byBrand.set(l.swell_brand_id, e);
    }

    resp.data = rows.map((r) => {
      const b = r.brand || {};
      const links = b.links || [];
      const logoUrl = (links.find((x) => x.is_primary) || links[0] || {}).b?.logo || null;
      const agg = byBrand.get(r.swell_brand_id) || { best: null, count: 0 };
      const best = agg.best || {};
      const score = best.swell_score || 0;
      // A brand-level deal value (set on the board) wins over the best lead's auto estimate.
      const deal = r.deal_value != null ? Number(r.deal_value) : (best.deal_value != null ? Number(best.deal_value) : null);
      const point = Number(best.spend_estimate) || 0;
      return {
        brandId: r.swell_brand_id,
        leadId: r.swell_brand_id, // card key — the pipeline is keyed by BRAND now
        name: b.name || "—",
        logo: swellInitials(b.name),
        logoUrl,
        color: swellColor(b.name),
        score,
        band: score >= 70 ? "A" : score >= 38 ? "B" : "C",
        occasion: best.recurring_window || "",
        leadCount: agg.count,
        emv: Number(deal ?? point) || 0,
        dealValue: deal,
        emvFmt: swellSpendRange({
          manual: deal != null, dealValue: deal, origin: best.origin,
          low: Number(best.spend_low) || 0, high: Number(best.spend_high) || 0, point,
        }),
        ownerId: r.owner_id || null,
        ownerName: r.owner ? [r.owner.first_name, r.owner.last_name].filter(Boolean).join(" ") : null,
        priority: r.priority,
        lastContactLabel: r.last_contact_label || null,
        nextSteps: r.next_steps || null,
        stage: r.bd_status,
      };
    }).sort((a, z) => z.score - a.score);
    return resp;
  },

  // Move a lead to a pipeline stage ('new_lead'|'engaged'|'proposal'|'negotiating'|'won'|'lost'),
  // or 'none' to pull it back out to the Leads list. RLS on swell_lead enforces who may.
  async setStage(leadId, stage) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_lead").update({ pipeline_stage: stage, updated_at: new Date().toISOString() }).eq("id", leadId);
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Brand-level outreach (BD tracking; one record per BRAND, mirroring the team's reach-out sheet) --
  // Patch any subset of fields; omitted keys are left unchanged server-side. Pass `owner: <id|null>`
  // explicitly to (re)assign or unassign the BD lead — omitting `owner` leaves the current one alone.
  async setOutreach(brandId, patch = {}) {
    const resp = { error: null };
    if (!brandId) { resp.error = "no brand"; return resp; }
    const hasOwner = Object.prototype.hasOwnProperty.call(patch, "owner");
    const hasDeal = Object.prototype.hasOwnProperty.call(patch, "dealValue");
    const { error } = await supabase.rpc("swell_set_outreach", {
      p_brand_id: brandId,
      p_status: patch.status ?? null,
      p_priority: patch.priority ?? null,
      p_campaign_status: patch.campaignStatus ?? null,
      p_last_contact_at: patch.lastContactAt ?? null,
      p_last_contact_label: patch.lastContactLabel ?? null,
      p_client_feedback: patch.clientFeedback ?? null,
      p_next_steps: patch.nextSteps ?? null,
      p_owner_id: hasOwner ? patch.owner : null,
      p_set_owner: hasOwner,
      p_deal_value: hasDeal ? patch.dealValue : null,
      p_set_deal: hasDeal,
    });
    if (error) resp.error = error.message;
    return resp;
  },

  // Manually set (or clear, with null) a lead's deal value — overrides the auto EMV in the pipeline.
  async setDealValue(leadId, value) {
    const resp = { error: null };
    const v = value === null || value === "" ? null : Number(value);
    const { error } = await supabase.from("swell_lead").update({ deal_value: Number.isFinite(v) ? v : null, updated_at: new Date().toISOString() }).eq("id", leadId);
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Handle approval queue ----------------------------------------------
  // Pending (unapproved) Instagram handles, grouped by brand, with the resolver's
  // confidence signals. Nothing pulls tagged posts until a handle is approved.
  async getPendingApprovals() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from(_HANDLE)
      .select(
        "id, handle, platform_pk, market_scope, is_verified, followers, full_name, external_url, resolution_confidence, content_score, " +
          "brand:swell_brand_id(id, name, website, resolution_status, review_flag), country:country_id(code, name)"
      )
      .eq("platform", "instagram")
      .eq("approval", "pending")
      .order("swell_brand_id", { ascending: true });
    if (error) {
      resp.error = error.message;
      return resp;
    }
    const groups = new Map();
    for (const h of data || []) {
      const b = h.brand || { id: "_", name: "—", resolution_status: null, review_flag: null };
      if (!groups.has(b.id)) groups.set(b.id, { brand: b, handles: [] });
      groups.get(b.id).handles.push({
        id: h.id,
        handle: h.handle,
        scope: h.market_scope,
        verified: h.is_verified,
        followers: h.followers,
        fullName: h.full_name,
        url: h.external_url,
        conf: h.resolution_confidence != null ? Number(h.resolution_confidence) : null,
        content: h.content_score != null ? Number(h.content_score) : null,
        country: h.country?.code || null,
        countryName: h.country?.name || null,
      });
    }
    resp.data = [...groups.values()].sort((a, z) => {
      const r = (g) => (g.brand.resolution_status === "resolved" ? 0 : 1);
      return r(a) - r(z) || a.brand.name.localeCompare(z.brand.name);
    });
    return resp;
  },

  // Approve / reject one or more handles. ids: number | number[]
  async setHandleApproval(ids, approval, userId) {
    const resp = { updated: 0, error: null };
    const list = Array.isArray(ids) ? ids : [ids];
    const { data: rows, error } = await supabase
      .from(_HANDLE)
      .update({ approval, approved_by: userId || null, approved_at: new Date().toISOString() })
      .in("id", list)
      .select("swell_brand_id");
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.updated = list.length;
    // Approving a handle RESOLVES its brand — otherwise the brand keeps resolution_status='review'
    // and re-appears in the Review filter on reload even though it has an approved handle.
    if (approval === "approved" && rows?.length) {
      const brandIds = [...new Set(rows.map((r) => r.swell_brand_id).filter(Boolean))];
      if (brandIds.length) {
        const { error: bErr } = await supabase
          .from("swell_brand")
          .update({ resolution_status: "resolved", review_flag: null })
          .in("id", brandIds);
        if (bErr) resp.error = bErr.message; // surface, don't silently leave it in Review
      }
    }
    return resp;
  },

  // Count of handles awaiting approval (nav badge).
  async getPendingCount() {
    const resp = { count: 0, error: null };
    const { count, error } = await supabase
      .from(_HANDLE)
      .select("id", { count: "exact", head: true })
      .eq("platform", "instagram")
      .eq("approval", "pending");
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.count = count || 0;
    return resp;
  },

  // -- Brands (all, searchable) -------------------------------------------
  // Every brand + its Instagram handles (any approval state), paginated and name-searchable.
  // Powers the Brands screen: approve/reject detected handles, or attach the right one by hand.
  async getBrandsPage({ search = "", page = 0, pageSize = 20, categoryId = null, status = null, group = "all" } = {}) {
    const resp = { data: [], total: 0, error: null };
    let q = supabase
      .from("swell_brand")
      .select(
        "id, name, website, resolution_status, review_flag, category:category_id(name), parent:parent_group_id(id, name), " +
          "handles:swell_brand_handle(id, handle, platform, market_scope, country_id, is_verified, followers, full_name, external_url, resolution_confidence, content_score, approval, operator:operator_group_id(id, name), country:country_id(code, name))",
        { count: "exact" }
      )
      .order("name", { ascending: true })
      .range(page * pageSize, page * pageSize + pageSize - 1);
    const term = (search || "").trim();
    if (term) {
      // match on brand name OR any of its instagram handles: find brand ids whose handle matches,
      // then OR them with the name filter.
      const { data: hh } = await supabase.from("swell_brand_handle").select("swell_brand_id").ilike("handle", `%${term}%`);
      const ids = [...new Set((hh || []).map((r) => r.swell_brand_id).filter(Boolean))];
      const idList = ids.length ? ids.join(",") : "0";
      q = q.or(`name.ilike.*${term}*,id.in.(${idList})`);
    }
    if (categoryId) q = q.eq("category_id", categoryId);
    if (status && status !== "all") q = q.eq("resolution_status", status);
    if (group === "independent") q = q.is("parent_group_id", null);
    else if (group && group !== "all") q = q.eq("parent_group_id", Number(group));
    const { data, count, error } = await q;
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.total = count || 0;
    resp.data = (data || []).map((b) => {
      const handles = (b.handles || [])
        .filter((h) => h.platform === "instagram")
        .map((h) => ({
          id: h.id,
          handle: h.handle,
          scope: h.market_scope,
          verified: h.is_verified,
          followers: h.followers,
          fullName: h.full_name,
          url: h.external_url,
          conf: h.resolution_confidence != null ? Number(h.resolution_confidence) : null,
          content: h.content_score != null ? Number(h.content_score) : null,
          approval: h.approval,
          countryId: h.country_id || null,
          country: h.country?.code || null,
          countryName: h.country?.name || null,
          operatorGroupId: h.operator?.id || null,   // franchisee that runs the brand in this market
          operatorGroupName: h.operator?.name || null,
        }))
        .sort((a, z) => (a.approval === "pending" ? 0 : 1) - (z.approval === "pending" ? 0 : 1));
      return {
        brand: { id: b.id, name: b.name, website: b.website, resolution_status: b.resolution_status, review_flag: b.review_flag, category: b.category?.name || null, parentGroupId: b.parent?.id || null, parentGroupName: b.parent?.name || null },
        handles,
        pendingCount: handles.filter((h) => h.approval === "pending").length,
      };
    });
    return resp;
  },

  // Roster — ONE page, server-side (reads the pre-aggregated MV). Scales regardless of total size.
  // Returns the page rows + totals (total / connected / notConnected / agency) for the KPIs & pager.
  async getRoster({ page = 1, pageSize = 100, connFilter = "all", search = "", agency = "all", minFollowers = 0, sort = "coverage", dir = "desc", kind = "all", country = "all" } = {}) {
    const resp = { data: [], total: 0, connected: 0, notConnected: 0, agency: 0, kinds: {}, creators: 0, brands: 0, agencies: 0, media: 0, community: 0, unknown: 0, error: null };
    const term = (search || "").trim() || null;
    const conn = connFilter === "in" ? "in" : connFilter === "out" ? "out" : "all";
    const ctry = country ? String(country) : "all";
    const [pageRes, statsRes] = await Promise.all([
      supabase.rpc("swell_roster", {
        p_limit: pageSize, p_offset: (page - 1) * pageSize, p_connected: conn,
        p_agency: agency, p_min_followers: minFollowers, p_search: term, p_sort: sort, p_dir: dir, p_kind: kind, p_country: ctry,
      }),
      supabase.rpc("swell_roster_stats", { p_connected: conn, p_agency: agency, p_min_followers: minFollowers, p_search: term, p_kind: kind, p_country: ctry }),
    ]);
    if (pageRes.error) { resp.error = pageRes.error.message; return resp; }
    resp.data = (pageRes.data || []).map((r) => {
      const followers = Number(r.follower_count) || 0;
      const avgLikes = Math.round(Number(r.avg_likes) || 0);
      const avgComments = Math.round(Number(r.avg_comments) || 0);
      const avgPlays = Math.round(Number(r.avg_plays) || 0);
      return {
        handle: r.handle,
        followers,
        connected: r.connected !== false,
        agency: r.agency || null,
        brandCount: r.brand_count || 0,
        postCount: r.post_count || 0,
        avgLikes, avgComments, avgPlays,
        engRate: r.eng_rate != null ? Number(r.eng_rate) : (followers ? +(((avgLikes + avgComments) / followers) * 100).toFixed(2) : 0),
        brands: Array.isArray(r.brands) ? r.brands : [],
        kind: r.kind || "unknown", // creator | public_figure | brand | agency | media | community | other | unknown
        igCategory: r.ig_category || null,
        fullName: r.full_name || null,
        verified: r.is_verified === true,
        brandTracked: r.brand_tracked === true, // for brand-kind rows: already in our swell_brand list
        country: r.country || null, // IG "based in" country (partial coverage), from creator enrichment
      };
    });
    const s = statsRes.data && statsRes.data[0];
    if (s) {
      resp.total = s.total || 0; resp.connected = s.connected || 0; resp.notConnected = s.not_connected || 0; resp.agency = s.agency || 0;
      const k = s.kinds || {};
      resp.kinds = k;
      resp.creators = (k.creator || 0) + (k.public_figure || 0); // real people you can hire
      resp.brands = k.brand || 0; resp.agencies = k.agency || 0;
      resp.media = k.media || 0; resp.community = k.community || 0;
      resp.unknown = (k.unknown || 0) + (k.other || 0);
    }
    return resp;
  },

  // Countries present in the roster (for the location filter dropdown), most creators first.
  async getRosterCountries() {
    const { data, error } = await supabase.rpc("swell_roster_countries");
    if (error) return [];
    return (data || []).map((r) => ({ id: String(r.country_id), name: r.name, count: r.cnt || 0 }));
  },

  // -- Tracked creators -----------------------------------------------------------------------
  // Tracking a creator makes Swell pull their OWN timeline, which is the only way to see the
  // brands they work with that we don't already track — collab posts never reach a brand's tagged
  // feed, and collabs are how paid work is published now.
  async trackCreator(handle) {
    const { error } = await supabase.rpc("swell_track_creator", { p_handle: handle });
    return { error: error ? error.message : null };
  },

  async untrackCreator(handle) {
    const { error } = await supabase.rpc("swell_untrack_creator", { p_handle: handle });
    return { error: error ? error.message : null };
  },

  // handle -> { status, backfillDone, postsSeen, brandsFound, untrackedFound, ... }
  async getTrackedCreators() {
    const resp = { data: new Map(), error: null };
    const { data, error } = await supabase.rpc("swell_tracked_creators");
    if (error) { resp.error = error.message; return resp; }
    for (const r of data || []) {
      resp.data.set(r.handle, {
        handle: r.handle,
        status: r.status,
        addedAt: r.added_at,
        lastPulledAt: r.last_pulled_at,
        backfillDone: r.backfill_done === true,
        postsSeen: r.posts_seen || 0,
        lastPostAt: r.last_post_at,
        error: r.last_error || null,
        brandsFound: r.brands_found || 0,
        untrackedFound: r.untracked_found || 0,
      });
    }
    return resp;
  },

  // Brands our tracked creators tag that we don't have. Ranked by collab + sponsor evidence —
  // "someone paid for this post" beats "mentioned a lot".
  async getCreatorBrandCandidates({ page = 1, pageSize = 100, search = "", commercialOnly = true } = {}) {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_creator_brand_candidates", {
      p_limit: pageSize,
      p_offset: (page - 1) * pageSize,
      p_search: (search || "").trim() || null,
      p_commercial_only: commercialOnly,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((r) => ({
      handle: r.handle,
      // IG display name — usually the real brand name ("Justlife UAE", "Castrol Middle East").
      // Null until the account has been profiled by swell-enrich-creators.
      name: r.full_name || null,
      igCategory: r.ig_category || null,
      posts: r.posts || 0,
      collabPosts: r.collab_posts || 0,
      sponsorPosts: r.sponsor_posts || 0,
      usertagPosts: r.usertag_posts || 0,
      mentionPosts: r.mention_posts || 0,
      creators: r.creators || 0,
      creatorHandles: r.creator_handles || [],
      firstSeen: r.first_seen,
      lastSeen: r.last_seen,
      kind: r.known_kind || null,
      followers: r.followers || null,
      // the headline: how many posts someone paid for
      paidPosts: (r.collab_posts || 0) + (r.sponsor_posts || 0),
    }));
    return resp;
  },

  // The creators behind a lead's "N creators / yr" chip. Date-bounded to the same window the chip
  // counts, so the dialog can't show a different set from the number that opened it.
  // limit 1000 = PostgREST's max_rows; asking for more returns no more.
  // minFollowers must match creatorMinFollowers in swell-score-leads, or the dialog will disagree
  // with the "worked with N creators" count that opened it.
  async getLeadCreatorList(leadId, { sinceDays = 365, limit = 1000, minFollowers = 100000 } = {}) {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_lead_creator_list", {
      p_lead_id: leadId, p_since_days: sinceDays, p_limit: limit, p_min_followers: minFollowers,
    });
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((c) => ({
      handle: c.handle,
      name: c.full_name || null,
      kind: c.kind || null,
      followers: c.followers || 0,
      posts: c.posts || 0,
      lastPost: c.last_post || null,
      inNetwork: c.in_network === true,
      occasions: c.occasions || [],
    }));
    return resp;
  },

  // Detail for one roster creator: their brand-tagged posts, the brands they've
  // tagged (aggregated from those posts), and the connected internal influencer
  // id (via the network table, falling back to a post's roster_influencer_id).
  async getCreatorDetail(handle) {
    const resp = { posts: [], brands: [], influencerId: null, creator: null, error: null };
    const h = (handle || "").trim().toLowerCase();
    if (!h) { resp.error = "No handle"; return resp; }
    const [postsRes, netRes, creatorRes] = await Promise.all([
      supabase
        .from("swell_brand_post")
        .select("id, code, taken_at, media_type, like_count, comment_count, play_count, is_paid_partnership, caption_text, roster_influencer_id, brand:swell_brand_id(id, name)")
        .eq("creator_handle", h)
        .order("taken_at", { ascending: false })
        .limit(48),
      supabase
        .from("network")
        .select("influencer_id")
        .ilike("username", h)
        .eq("social_network", "instagram")
        .eq("status", "approved")
        .limit(1),
      supabase
        .from("swell_creator")
        .select("handle, followers, full_name, is_verified, ig_category, kind, biography, external_url")
        .eq("handle", h)
        .maybeSingle(),
    ]);
    if (postsRes.error) { resp.error = postsRes.error.message; return resp; }
    const posts = postsRes.data || [];
    resp.posts = posts.map((p) => ({
      id: p.id,
      code: p.code || null,
      takenAt: p.taken_at,
      mediaType: p.media_type,
      likes: p.like_count || 0,
      comments: p.comment_count || 0,
      plays: p.play_count || 0,
      paid: p.is_paid_partnership === true,
      caption: p.caption_text || "",
      brand: p.brand ? p.brand.name : null,
    }));
    // Brands they tag — counted per POST, from two sources.
    //
    // `p.brand` is only the brand whose TAGGED FEED this post arrived through, so on its own it
    // undercounts badly: a creator who tagged 18 of our brands across 6 posts showed 5, because the
    // other 17 were co-tags on those same posts. swell_post_cotag already holds them; they just have
    // to be resolved back to swell_brand. Mirrors what swell_roster_mv now does for the list view
    // (migration 20260910180000) — the two must agree or the row and its drawer contradict.
    const byBrand = new Map();     // name   -> Set(postId), so a brand tagged twice in one post counts once
    const byPost = new Map();      // postId -> Set(name),   every tracked brand tagged in that post
    const add = (name, postId) => {
      if (!name) return;
      if (!byBrand.has(name)) byBrand.set(name, new Set());
      byBrand.get(name).add(postId);
      if (!byPost.has(postId)) byPost.set(postId, new Set());
      byPost.get(postId).add(name);
    };
    for (const p of posts) add(p.brand ? p.brand.name : null, p.id);

    const postIds = posts.map((p) => p.id);
    if (postIds.length) {
      const { data: cotags } = await supabase
        .from("swell_post_cotag")
        .select("post_id, cotagged_handle")
        .in("post_id", postIds);
      const rows = cotags || [];
      const handles = [...new Set(rows.map((c) => (c.cotagged_handle || "").toLowerCase()).filter(Boolean))];
      if (handles.length) {
        // 'rejected' means a human decided that handle is NOT that brand — counting it would
        // attribute the tag to the wrong company.
        const { data: known } = await supabase
          .from("swell_brand_handle")
          .select("handle, brand:swell_brand_id(name), approval")
          .in("handle", handles)
          .neq("approval", "rejected");
        const nameByHandle = new Map(
          (known || []).filter((k) => k.brand).map((k) => [k.handle.toLowerCase(), k.brand.name]),
        );
        for (const c of rows) add(nameByHandle.get((c.cotagged_handle || "").toLowerCase()), c.post_id);
      }
    }

    resp.brands = [...byBrand.entries()]
      .map(([name, set]) => ({ name, count: set.size }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    // Attach every tracked brand tagged in each post, not just the one whose feed it arrived
    // through — otherwise six posts each showing a single chip read as "he only works with five
    // brands", which is the impression that started all of this. Originating brand stays first.
    for (const p of resp.posts) {
      const all = [...(byPost.get(p.id) || [])];
      p.brands = p.brand ? [p.brand, ...all.filter((n) => n !== p.brand)] : all.sort();
    }
    const netId = netRes.data && netRes.data[0] ? netRes.data[0].influencer_id : null;
    const postId = (posts.find((p) => p.roster_influencer_id) || {}).roster_influencer_id || null;
    resp.influencerId = netId || postId || null;
    resp.creator = creatorRes.data || null;
    return resp;
  },

  // Discovery: accounts co-tagged alongside our brands that AREN'T in our brand list yet.
  async getDiscovery({ page = 1, pageSize = 100, search = "", sort = "freq", dir = "desc", hideNoise = true, kind = "all", minFollowers = 0, minFreq = 0, categories = [], countries = [] } = {}) {
    const resp = { data: [], total: 0, newWeek: 0, topFreq: 0, topHandle: null, noiseCount: 0, error: null };
    const term = (search || "").trim() || null;
    const cats = Array.isArray(categories) && categories.length ? categories : null;
    const ctys = Array.isArray(countries) && countries.length ? countries : null;
    const [pageRes, statsRes] = await Promise.all([
      supabase.rpc("swell_discovery", {
        p_limit: pageSize, p_offset: (page - 1) * pageSize, p_search: term, p_sort: sort, p_dir: dir, p_hide_noise: hideNoise,
        p_kind: kind, p_min_followers: minFollowers, p_min_freq: minFreq, p_category: cats, p_country: ctys,
      }),
      supabase.rpc("swell_discovery_stats", { p_search: term, p_hide_noise: hideNoise, p_kind: kind, p_min_followers: minFollowers, p_min_freq: minFreq, p_category: cats, p_country: ctys }),
    ]);
    if (pageRes.error) { resp.error = pageRes.error.message; return resp; }
    resp.data = (pageRes.data || []).map((r) => ({
      handle: r.handle,
      freq: r.freq || 0,
      brandCount: r.brand_count || 0,
      brands: Array.isArray(r.brands) ? r.brands : [],
      firstSeen: r.first_seen || null,
      isNoise: r.is_noise === true,
      isNew: r.first_seen ? (Date.now() - new Date(r.first_seen).getTime()) < 7 * 864e5 : false,
      name: r.full_name || null,      // IG display name (usually the brand's real name)
      igCategory: r.ig_category || null,
      country: r.country || null,     // resolved IG "based in" country (partial coverage)
      kind: r.kind || null,           // creator | brand | agency | ... (null = not yet enriched)
      followers: Number(r.followers) || 0,
      tracked: r.tracked === true,    // already exists in our swell_brand list
    }));
    const s = statsRes.data && statsRes.data[0];
    if (s) { resp.total = s.total || 0; resp.newWeek = s.new_week || 0; resp.topFreq = s.top_freq || 0; resp.topHandle = s.top_handle || null; resp.noiseCount = s.noise_count || 0; }
    return resp;
  },

  // Distinct IG categories present in the live discovery feed (for the category multi-select filter).
  async getDiscoveryCategories() {
    const { data, error } = await supabase.rpc("swell_discovery_categories");
    if (error) return [];
    return (data || []).map((r) => r.category).filter(Boolean);
  },

  // Distinct resolved countries present in the live discovery feed (for the country multi-select filter).
  async getDiscoveryCountries() {
    const { data, error } = await supabase.rpc("swell_discovery_countries");
    if (error) return [];
    return (data || []).map((r) => r.country).filter(Boolean);
  },

  // Persist an add/dismiss decision so the handle drops out of the discovery feed.
  async setDiscoveryStatus(handle, status) {
    const { error } = await supabase.rpc("swell_discovery_set_status", { p_handle: handle, p_status: status });
    return { error: error ? error.message : null };
  },

  // Promote a discovered handle into the brand list, then mark it added. Handle-only: the server
  // enriches, derives the brand name from the IG profile, and de-dupes (reuses an existing brand
  // when the handle is already tracked or the normalized name matches) so no duplicate brand is made.
  async addDiscoveryToWatchlist(handle, name = null, swellBrandId = null, approvedBy = null) {
    const resp = { error: null, warning: null, brandName: null, brandCreated: false, alreadyTracked: false };
    const { data, error } = await supabase.functions.invoke("swell-add-handle", {
      body: { handle, swell_brand_id: swellBrandId || null, brand_name: (name || "").trim() || null, market_scope: "gcc", approved_by: approvedBy },
    });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch (_e) { /* ignore */ }
      resp.error = msg;
      return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    await this.setDiscoveryStatus(handle, "added");
    resp.brandName = data?.brand_name || null;
    resp.brandCreated = data?.brand_created === true;
    resp.alreadyTracked = data?.already_tracked === true;
    resp.warning = data?.enriched ? null : data?.enrichWarning || null;
    return resp;
  },

  // Rename a brand (updates name + normalized_name so dedup keeps working).
  async renameBrand(brandId, name) {
    const resp = { error: null };
    const nm = (name || "").trim();
    if (!nm) { resp.error = "Name is required"; return resp; }
    const norm = nm.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const { error } = await supabase.from("swell_brand").update({ name: nm, normalized_name: norm }).eq("id", brandId);
    if (error) resp.error = error.message;
    return resp;
  },

  // Set (or clear, with null) a HANDLE's market operator — the franchisee that runs the brand in
  // that country. Manual curation; marks confidence + timestamp.
  async setHandleOperator(handleId, groupId) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_brand_handle")
      .update({ operator_group_id: groupId || null, operator_confidence: groupId ? 1 : null, operator_set_at: new Date().toISOString() })
      .eq("id", handleId);
    if (error) resp.error = error.message;
    return resp;
  },

  // Parent-group list for the group editors (id + name + brand count, alphabetical).
  async getParentGroups() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_parent_groups");
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((g) => ({ id: g.id, name: g.name, brandCount: g.brand_count || 0 }));
    return resp;
  },

  // Manually set (or clear, with null) a brand's parent group. Stamps group_classified_at so the
  // map-only classifier cron respects the human choice and won't re-map it.
  // Pass `newName` instead of an id to find-or-create a group by name (Reach outs table typing).
  // Returns the resulting group id so callers can update their local row.
  async setBrandGroup(brandId, groupId, newName = null) {
    const resp = { groupId: null, error: null };
    let gid = groupId || null;
    if (!gid && newName) {
      const { data, error } = await supabase.rpc("swell_set_brand_group", { p_brand_id: brandId, p_group_id: null, p_new_name: newName });
      if (error) { resp.error = error.message; return resp; }
      gid = data ?? null;
    } else {
      const { error } = await supabase.from("swell_brand").update({ parent_group_id: gid }).eq("id", brandId);
      if (error) { resp.error = error.message; return resp; }
    }
    // Mark the assignment as human-made so swell-classify-group leaves it alone.
    const { error: sErr } = await supabase.from("swell_brand")
      .update({ parent_group_confidence: gid ? 1 : null, group_classified_at: new Date().toISOString() })
      .eq("id", brandId);
    if (sErr) { resp.error = sErr.message; return resp; }
    resp.groupId = gid;
    return resp;
  },

  // Category list for the Brands filter (name + id).
  async getCategories() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from("swell_brand_category")
      .select("id, name")
      .order("name", { ascending: true });
    if (error) resp.error = error.message;
    else resp.data = data || [];
    return resp;
  },

  // Manually add a news lead from an article URL (server fetches, classifies, scores + briefs it).
  async addNewsLead(url) {
    const resp = { data: null, warning: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-add-news-lead", { body: { url } });
    if (error) {
      // edge function returns a JSON error body on non-2xx — surface its message if present
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch (_e) { /* ignore */ }
      resp.error = msg;
      return resp;
    }
    if (data?.error) { resp.error = data.error; return resp; }
    resp.data = data || null;
    resp.warning = data?.warning || null;
    return resp;
  },

  // Manually attach the correct handle to a brand (auto-approved + IG-enriched, server-side).
  // Pass brandName (instead of swellBrandId) to create a brand-new brand with this handle.
  async addHandleManual({ swellBrandId = null, brandName = null, handle, marketScope = "country", countryId = null, approvedBy = null }) {
    const resp = { data: null, warning: null, error: null };
    const { data, error } = await supabase.functions.invoke("swell-add-handle", {
      body: { swell_brand_id: swellBrandId, brand_name: brandName, handle, market_scope: marketScope, country_id: countryId, approved_by: approvedBy },
    });
    if (error) {
      resp.error = error.message || String(error);
      return resp;
    }
    if (data?.error) {
      resp.error = data.error;
      return resp;
    }
    resp.data = data?.handle || null;
    resp.warning = data?.enriched ? null : data?.enrichWarning || null;
    return resp;
  },

  // Change a handle's market scope (and country when scope=country).
  // -- Proposed market partners (distributors) ------------------------------------------------
  // swell-find-distributor writes PENDING rows only; accepting one here is the only path from a
  // machine guess to a live operator_group_id. See migration 20260917130000 for why.
  // Distributors for ONE brand, grouped — used by the drawer, which opens a single lead.
  async getLeadPartners(brandId) {
    if (!brandId) return [];
    const m = await loadDistributors([brandId]);
    return m.get(brandId) || [];
  },

  async getPendingPartners(limit = 200) {
    const resp = { data: [], error: null };
    const { data, error } = await supabase.rpc("swell_pending_partners", { p_limit: limit });
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).map((r) => ({
      id: r.id,
      brandId: r.brand_id,
      brand: r.brand_name,
      country: r.country_code,   // 'ALL' = the default row, covering every unnamed market
      role: r.role,
      group: r.group_name,
      domain: r.group_domain || null,
      evidence: r.evidence_url || null,
      confidence: r.confidence == null ? null : Number(r.confidence),
      proposedAt: r.proposed_at,
      note: r.note || null,   // relationship word + verbatim quote from the evidence page
    }));
    return resp;
  },

  async reviewPartner(id, accept) {
    const { error } = await supabase.rpc("swell_review_market_partner", { p_id: id, p_accept: !!accept });
    return { error: error ? error.message : null };
  },

  async findDistributors({ brandId = null, limit = 8, dryRun = false } = {}) {
    const { data, error } = await supabase.functions.invoke("swell-find-distributor", {
      body: { swell_brand_id: brandId || undefined, limit, dry_run: dryRun || undefined },
    });
    if (error) {
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch (_e) { /* not JSON */ }
      return { data: null, error: msg };
    }
    if (data?.error) return { data: null, error: data.error };
    return { data, error: null };
  },

  async updateHandleMarket({ handleId, marketScope, countryId }) {
    const resp = { error: null };
    const { error } = await supabase
      .from(_HANDLE)
      .update({ market_scope: marketScope, country_id: marketScope === "country" ? countryId : null })
      .eq("id", handleId);
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Signal sources ------------------------------------------------------
  async getSources() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from(_SOURCE)
      .select("name, source_group, channel, endpoint, method, status, planned, last_pull_at")
      .order("id");
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.data = (data || []).map((s) => ({
      name: s.name,
      group: s.source_group,
      channel: s.channel,
      endpoint: s.endpoint,
      method: s.method,
      status: s.status,
      planned: s.planned,
      last: s.last_pull_at ? new Date(s.last_pull_at).toLocaleString() : "—",
      items: 0,
    }));
    return resp;
  },

  // -- Marketing calendar --------------------------------------------------
  // Returns occasions in the recurrence-rule shape buildCalendar() expects, so the
  // SPA computes dates for any year client-side (Hijri/Easter/etc.) from DB data —
  // identical to the local mena_calendar.json shape: date_type + the rule fields.
  async getCalendar() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from(_OCCASION)
      .select("id, occasion, audience, tier, keywords_en, keywords_ar, markets_raw, date_type, recurrence")
      .eq("active", true)
      .order("id");
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.data = (data || []).map((o) => ({
      id: o.id,
      occasion: o.occasion,
      audience: o.audience,
      tier: o.tier,
      markets: o.markets_raw,
      keywords: [...(o.keywords_en || []), ...(o.keywords_ar || [])],
      date_type: o.date_type,
      ...(o.recurrence || {}), // rule fields (hmonth/hday/duration, month/day, start_month…)
    }));
    return resp;
  },

  // Add a custom occasion to track. `o` carries the buildCalendar rule fields:
  // { occasion, audience, tier, markets, keywords_en[], keywords_ar[], date_type, recurrence }
  async addOccasion(o) {
    const resp = { data: null, error: null };
    const { data, error } = await supabase
      .from(_OCCASION)
      .insert({
        occasion: o.occasion,
        audience: o.audience,
        tier: o.tier,
        keywords_en: o.keywords_en || [],
        keywords_ar: o.keywords_ar || [],
        markets_raw: o.markets || null,
        date_type: o.date_type,
        recurrence: o.recurrence || {},
        active: true,
      })
      .select("id, occasion, audience, tier, keywords_en, keywords_ar, markets_raw, date_type, recurrence")
      .single();
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.data = {
      id: data.id,
      occasion: data.occasion,
      audience: data.audience,
      tier: data.tier,
      markets: data.markets_raw,
      keywords: [...(data.keywords_en || []), ...(data.keywords_ar || [])],
      date_type: data.date_type,
      ...(data.recurrence || {}),
    };
    return resp;
  },

  // Delete a custom (or seeded) occasion by id.
  async deleteOccasion(id) {
    const resp = { error: null };
    const { error } = await supabase.from(_OCCASION).delete().eq("id", id);
    if (error) resp.error = error.message;
    return resp;
  },

  // -- Region coverage (markets) ------------------------------------------
  // Each market FKs country(id); `label` is the short display name (UAE/KSA), falling
  // back to the country's official name.
  async getMarkets() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from(_MARKET)
      .select("id, country_id, label, active, country:country_id(name, code)")
      .order("sort_order", { ascending: true })
      .order("id", { ascending: true });
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.data = (data || []).map((m) => ({
      id: m.id,
      country_id: m.country_id,
      code: m.country?.code || null,
      name: m.label || m.country?.name || "",
      on: m.active,
    }));
    return resp;
  },

  // Resolve the typed text to a country (by ISO2 code or name), then add it. The
  // unique(country_id) constraint blocks adding the same country twice.
  async addMarket(input) {
    const resp = { data: null, error: null };
    const q = (input || "").trim();
    if (!q) {
      resp.error = "Enter a country";
      return resp;
    }
    const { data: matches, error: cErr } = await supabase
      .from("country")
      .select("id, name, code")
      .or(`code.ilike.${q},name.ilike.%${q}%`)
      .limit(5);
    if (cErr) {
      resp.error = cErr.message;
      return resp;
    }
    if (!matches || !matches.length) {
      resp.error = `No country matches "${q}"`;
      return resp;
    }
    const exact = matches.find((c) => c.code.toLowerCase() === q.toLowerCase() || c.name.toLowerCase() === q.toLowerCase());
    const country = exact || matches[0];
    const { data, error } = await supabase
      .from(_MARKET)
      .insert({ country_id: country.id, label: q, active: true })
      .select("id, country_id, label, active, country:country_id(name, code)")
      .single();
    if (error) {
      resp.error = error.code === "23505" ? `${country.name} is already covered` : error.message;
      return resp;
    }
    resp.data = {
      id: data.id,
      country_id: data.country_id,
      code: data.country?.code || country.code,
      name: data.label || data.country?.name || country.name,
      on: data.active,
    };
    return resp;
  },

  // Typeahead over the country table (by name or ISO2 code), excluding already-added ids.
  async searchCountries(query, excludeIds = []) {
    const resp = { data: [], error: null };
    const term = (query || "").trim();
    if (!term) return resp;
    const { data, error } = await supabase
      .from("country")
      .select("id, name, code")
      .or(`name.ilike.%${term}%,code.ilike.${term}`)
      .order("name", { ascending: true })
      .limit(8);
    if (error) {
      resp.error = error.message;
      return resp;
    }
    resp.data = (data || []).filter((c) => !excludeIds.includes(c.id));
    return resp;
  },

  // Add a market for a specific country chosen from the autocomplete.
  async addMarketByCountry(country) {
    const resp = { data: null, error: null };
    const { data, error } = await supabase
      .from(_MARKET)
      .insert({ country_id: country.id, label: country.name, active: true })
      .select("id, country_id, label, active, country:country_id(name, code)")
      .single();
    if (error) {
      resp.error = error.code === "23505" ? `${country.name} is already covered` : error.message;
      return resp;
    }
    resp.data = {
      id: data.id,
      country_id: data.country_id,
      code: data.country?.code || country.code,
      name: data.label || data.country?.name || country.name,
      on: data.active,
    };
    return resp;
  },

  async setMarketActive(id, active) {
    const resp = { error: null };
    const { error } = await supabase.from(_MARKET).update({ active }).eq("id", id);
    if (error) resp.error = error.message;
    return resp;
  },

  async removeMarket(id) {
    const resp = { error: null };
    const { error } = await supabase.from(_MARKET).delete().eq("id", id);
    if (error) resp.error = error.message;
    return resp;
  },

  // Signed-in user's id (null when signed out). Used to tell "owned by me" from "owned by someone
  // else" in the UI without re-deriving it from a permissions payload.
  async getMyUserId() {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id || null;
  },

  // -- Members & permissions ----------------------------------------------
  // The current user's Swell permissions (null = not a member = no access).
  async getMyPermissions() {
    const resp = { data: null, error: null };
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) { resp.error = "not signed in"; return resp; }
    const { data, error } = await supabase.from("swell_member").select("*").eq("user_id", uid).maybeSingle();
    if (error) resp.error = error.message;
    else resp.data = data;
    return resp;
  },

  // All members + their user info (admin-only via RLS).
  async getMembers() {
    const resp = { data: [], error: null };
    const { data, error } = await supabase
      .from("swell_member")
      .select("user_id, is_admin, leads, pipeline, insights, roster, contacts, brands, user:user_id(first_name, last_name, email, avatar)")
      .order("created_at", { ascending: true });
    if (error) resp.error = error.message;
    else resp.data = data || [];
    return resp;
  },

  // Members a lead can be assigned to — usable by admins OR leads='all' members (names/ids only,
  // no permission flags). Shaped like getMembers so the assign dropdown / memberName() work unchanged.
  async getAssignableMembers() {
    const { data, error } = await supabase.rpc("swell_assignable_members");
    if (error) return { data: [], error: error.message };
    return {
      data: (data || []).map((r) => ({ user_id: r.user_id, user: { first_name: r.first_name, last_name: r.last_name, email: r.email } })),
      error: null,
    };
  },

  // Staff users not yet members (for the add-member picker).
  async searchStaffUsers(query, excludeIds = []) {
    const resp = { data: [], error: null };
    const term = (query || "").trim();
    if (!term) return resp;
    const { data, error } = await supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .eq("user_type", "staff")
      .eq("deactivate", false)
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`)
      .limit(8);
    if (error) { resp.error = error.message; return resp; }
    resp.data = (data || []).filter((u) => !excludeIds.includes(u.id));
    return resp;
  },

  async addMember({ userId, isAdmin = false, leads = "own", pipeline = "own", insights = false, roster = false, contacts = false, brands = false }) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_member").insert({
      user_id: userId, is_admin: isAdmin, leads, pipeline, insights, roster, contacts, brands,
    });
    if (error) resp.error = error.code === "23505" ? "Already a member" : error.message;
    return resp;
  },

  async updateMember(userId, patch) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_member").update({ ...patch, updated_at: new Date().toISOString() }).eq("user_id", userId);
    if (error) resp.error = error.message;
    return resp;
  },

  async removeMember(userId) {
    const resp = { error: null };
    const { error } = await supabase.from("swell_member").delete().eq("user_id", userId);
    if (error) resp.error = error.message;
    return resp;
  },
};
